import React from "react";
import {Box, Chip, Collapse, IconButton, Stack, TableCell, TableRow, Tooltip, Typography} from "@mui/material";
import {CheckCircleOutline, ExpandMore, HighlightOff, KeyboardArrowRight} from "@mui/icons-material";
import type {ChangeStep} from "~/prototypes/pages/taxonomy-canvas/approval/changeSteps.ts";
import {CHANGE_KIND_META} from "~/prototypes/pages/taxonomy-canvas/approval/changeSteps.ts";
import {buildDiffGraph} from "~/prototypes/pages/taxonomy-canvas/approval/changeDiffGraph.ts";
import {ChangeDiffCanvas} from "~/prototypes/pages/taxonomy-canvas/approval/ChangeDiffCanvas.tsx";
import {EditAuthorChip} from "~/prototypes/pages/taxonomy-canvas/approval/EditAuthorChip.tsx";

const N_COLUMNS = 6;

type Props = {
    step: ChangeStep
    index: number
    /** Accept this change: it becomes part of the baseline */
    onAccept: () => void
    /** Reject this change: it is undone on the canvas */
    onReject: () => void
}
/** One change: a summary row that opens to a picture of the affected categories, before and after combined */
export const TaxonomyChangeRow: React.FC<Props> = ({step, index, onAccept, onReject}) => {
    const [open, setOpen] = React.useState(false);
    // React Flow fits the view once, on mount: mount the canvases only after the row has finished opening,
    // otherwise they fit into the still-growing (tiny) box
    const [entered, setEntered] = React.useState(false);
    const meta = CHANGE_KIND_META[step.kind];
    const canOpen = step.applies;

    // Only built while the row is open
    // Built as soon as the row opens (its height sizes the open animation); the canvas mounts once it has opened
    const graph = React.useMemo(() => open ? buildDiffGraph(step) : null, [open, step]);

    return <>
        <TableRow
            hover={canOpen}
            onClick={canOpen ? () => setOpen(!open) : undefined}
            sx={{cursor: canOpen ? 'pointer' : 'default', '& > td': {borderBottom: open ? 'none' : undefined}}}
        >
            <TableCell padding="checkbox">
                {canOpen && <IconButton size="small" aria-label={open ? 'Hide details' : 'Show details'}>
                    {open ? <ExpandMore/> : <KeyboardArrowRight/>}
                </IconButton>}
            </TableCell>
            <TableCell sx={{width: 40, color: 'text.secondary'}}>{index + 1}</TableCell>
            <TableCell sx={{width: 120}}>
                <Chip label={meta.label} color={meta.color} variant="outlined" size="small"/>
            </TableCell>
            <TableCell>
                <Typography variant="body2" fontWeight={600}>{step.category}</Typography>
                <Typography variant="caption" color={step.applies ? 'text.secondary' : 'warning.main'}>
                    {step.summary}
                </Typography>
            </TableCell>
            <TableCell sx={{width: 200}}>
                <EditAuthorChip author={step.edit.author}/>
            </TableCell>
            <TableCell align="right" sx={{width: 100, whiteSpace: 'nowrap'}}>
                {/* Buttons don't open / close the row */}
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" onClick={e => e.stopPropagation()}>
                    <Tooltip title={step.applies ? 'Accept this change' : 'This change no longer applies'}>
                        <span>
                            <IconButton size="small" color="success" aria-label="Accept this change"
                                        disabled={!step.applies} onClick={onAccept}>
                                <CheckCircleOutline fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Reject this change (undo it on the canvas)">
                        <IconButton size="small" color="error" aria-label="Reject this change" onClick={onReject}>
                            <HighlightOff fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </TableCell>
        </TableRow>
        <TableRow>
            <TableCell colSpan={N_COLUMNS} sx={{py: 0, ...(open ? {} : {borderBottom: 'none'})}}>
                <Collapse
                    in={open}
                    timeout="auto"
                    unmountOnExit
                    onEntered={() => setEntered(true)}
                    onExited={() => setEntered(false)}
                >
                    {/* Fixed height so the open animation is smooth */}
                    <Box sx={{height: graph?.height ?? 0, py: 2, boxSizing: 'content-box'}}>
                        {graph && entered && <ChangeDiffCanvas nodes={graph.nodes} edges={graph.edges} height={graph.height}/>}
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    </>
}
